import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setLayout } from '../store/slices/layoutSlice'
import { GAMES } from '@core/appearance/games'
import { INTERNAL_CONSTANTS } from '@constants/internalConstants'

/**
 * useResponsiveLayout
 * -------------------
 * Watches the viewport and writes the active `mode`, `duelBetMode` and the per-component
 * pixel heights into the layout slice. `canvas` always soaks up whatever height is left.
 *
 * To add a new layout (STANDARD ships today; UNIQUE — per-game heights — comes later):
 *   1. Add a heights table (see STANDARD_HEIGHTS).
 *   2. Add a resolver (see resolveStandard) returning { duelBetMode, gap, sizes }.
 *   3. Register it in LAYOUT_RESOLVERS under its layout key.
 * The viewport→mode detection and the canvas/gap math are shared and stay untouched.
 * A resolver also receives `gameCode`, so a UNIQUE resolver can branch its heights per game.
 */

// ---- Viewport → mode ----
const TABLET_BREAKPOINT = 651
const DESKTOP_BREAKPOINT = 1221
// The embedded MINI widget is nominally 320×400. Match it with a tolerance so an
// exact-boundary measurement (321×400, 320×401…) from DPR rounding, a scrollbar,
// or a transient mount read still resolves to MINI instead of silently falling
// through to MOBILE. The HEIGHT ceiling is the real guard: every portrait phone
// is far taller (≥560) and landscape phones are far wider, so no real device
// lands inside this small envelope.
const MINI = { width: 360, height: 460 } // at/below this → embedded MINI layout

const detectMode = (vw, vh) => {
  if (vw <= MINI.width && vh <= MINI.height) return 'MINI'
  if (vw >= DESKTOP_BREAKPOINT) return 'DESKTOP'
  if (vw >= TABLET_BREAKPOINT) return 'TABLET'
  return 'MOBILE'
}

// ---- Shared math ----
// canvas = working height − header − the other stacked heights − every marginBottom.
// Index renders a `marginBottom: gap` on EVERY stacked element — the header, each panel
// AND the canvas — so the gap count is (#other panels + 2): +1 for the canvas's own
// margin, +1 for the gap that sits below the header.
const computeCanvas = (workingHeight, header, stacked, gap) => {
  const present = stacked.filter((h) => h != null)
  const stackedSum = present.reduce((sum, h) => sum + h, 0)
  const gapCount = present.length + 2
  return Math.max(workingHeight - header - stackedSum - gap * gapCount, 0)
}

const makeSizes = ({
  header = null,
  lastCrashMultipliers = null,
  canvas = null,
  jackpot = null,
  bettingPanel = null,
  activityPanel = null,
}) => ({
  header: { height: header },
  lastCrashMultipliers: { height: lastCrashMultipliers },
  canvas: { height: canvas },
  jackpot: { height: jackpot },
  bettingPanel: { height: bettingPanel },
  activityPanel: { height: activityPanel },
})

// ============================================================================
// STANDARD layout
// ============================================================================

// Duel betting is a STANDARD/MOBILE-only concern: use the tall two-session panel
// only when it still leaves a usable canvas; otherwise fall back to a single panel.
const SINGLE_BETPANEL_HEIGHT = 202
const DUEL_BETPANEL_HEIGHT = 374
const MIN_CANVAS_HEIGHT = 300
const DUEL_ON = { classic: true, freebets: true, divider: true }
const DUEL_OFF = { classic: false, freebets: false, divider: false }

// Static heights (px) per mode. `canvas` is computed, so it is never listed here.
//   gap          — vertical space between stacked panels (Index renders it as marginBottom)
//   bottomOffSet — space kept below the last panel; shrinks the working height, so the
//                  computed canvas ends short of the viewport and the gap falls at the bottom
const STANDARD_HEIGHTS = {
  // MINI: `lastCrashMultipliers` reserves the "Recent Results" label + row; the
  // bet card holds the presets/input grid + the tall action button.
  MINI: { header: 50, lastCrashMultipliers: 40, bettingPanel: 120, gap: 10, bottomOffSet: 10 },
  MOBILE: { header: 50, lastCrashMultipliers: 25, jackpot: 25, gap: 10, bottomOffSet: 15 },
  TABLET: {
    header: 50,
    lastCrashMultipliers: 25,
    jackpot: 25,
    bettingPanel: SINGLE_BETPANEL_HEIGHT,
    gap: 10,
    bottomOffSet: 15,
  },
  DESKTOP: { header: 50, lastCrashMultipliers: 25, jackpot: 25, gap: 10, bottomOffSet: 10 },
}

const resolveStandard = (mode, { vh, jackpotEnabled }) => {
  const h = STANDARD_HEIGHTS[mode]
  const workingHeight = vh - h.bottomOffSet
  const jackpot = jackpotEnabled ? h.jackpot : null

  // DESKTOP: a 3-column CSS grid owns canvas / betting / activity, so only the
  // pieces stacked outside the grid get an explicit height.
  if (mode === 'DESKTOP') {
    return {
      duelBetMode: DUEL_ON,
      gap: h.gap,
      sizes: { header: h.header, lastCrashMultipliers: h.lastCrashMultipliers, jackpot },
    }
  }

  // MINI: single panel, no jackpot / activity.
  if (mode === 'MINI') {
    const canvas = computeCanvas(
      workingHeight,
      h.header,
      [h.lastCrashMultipliers, h.bettingPanel],
      h.gap
    )
    return {
      duelBetMode: DUEL_OFF,
      gap: h.gap,
      sizes: {
        header: h.header,
        lastCrashMultipliers: h.lastCrashMultipliers,
        canvas,
        bettingPanel: h.bettingPanel,
      },
    }
  }

  // TABLET: single panel, no duel gate.
  if (mode === 'TABLET') {
    const canvas = computeCanvas(
      workingHeight,
      h.header,
      [h.lastCrashMultipliers, jackpot, h.bettingPanel],
      h.gap
    )
    return {
      duelBetMode: { classic: true, freebets: true, divider: false },
      gap: h.gap,
      sizes: {
        header: h.header,
        lastCrashMultipliers: h.lastCrashMultipliers,
        canvas,
        jackpot,
        bettingPanel: h.bettingPanel,
      },
    }
  }

  // MOBILE: pick the duel panel only if a usable canvas still fits underneath it.
  const duelCanvas = computeCanvas(
    workingHeight,
    h.header,
    [h.lastCrashMultipliers, jackpot, DUEL_BETPANEL_HEIGHT],
    h.gap
  )
  const isDuel = duelCanvas >= MIN_CANVAS_HEIGHT
  const bettingPanel = isDuel ? DUEL_BETPANEL_HEIGHT : SINGLE_BETPANEL_HEIGHT
  const canvas = isDuel
    ? duelCanvas
    : computeCanvas(workingHeight, h.header, [h.lastCrashMultipliers, jackpot, bettingPanel], h.gap)

  return {
    duelBetMode: isDuel ? DUEL_ON : DUEL_OFF,
    gap: h.gap,
    sizes: {
      header: h.header,
      lastCrashMultipliers: h.lastCrashMultipliers,
      canvas,
      jackpot,
      bettingPanel,
    },
  }
}

// ============================================================================
// Layout registry — the extension point for UNIQUE (per-game) layouts.
// A resolver takes (mode, { vh, jackpotEnabled, gameCode }) → { duelBetMode, gap, sizes }.
// ============================================================================
const LAYOUT_RESOLVERS = {
  STANDARD: resolveStandard,
  // UNIQUE: resolveUnique, // per-game heights — coming later
}

const emptyLayout = () => ({
  mode: 'DESKTOP',
  duelBetMode: DUEL_ON,
  dimensions: { vw: 0, vh: 0, gap: 0, sizes: makeSizes({}) },
})

const computeLayout = ({ layout, vw, vh, jackpotEnabled, gameCode }) => {
  // Pre-hydration / SSR fallback
  if (!vw || !vh) return emptyLayout()

  const mode = detectMode(vw, vh)
  const resolve = LAYOUT_RESOLVERS[layout] ?? resolveStandard
  const { duelBetMode, gap, sizes } = resolve(mode, { vh, jackpotEnabled, gameCode })

  return {
    mode,
    duelBetMode,
    dimensions: { vw, vh, gap, sizes: makeSizes(sizes) },
  }
}

export const useResponsiveLayout = () => {
  const dispatch = useDispatch()
  const gameCode = useSelector((state) => state.launchParams.params.gameCode)
  // The layout family (STANDARD/UNIQUE) comes from the game registry, not a
  // launch param — it picks which heights resolver runs.
  const layout = GAMES[gameCode]?.layout ?? INTERNAL_CONSTANTS.GAME_LAYOUTS.STANDARD
  const isJackpotEnabled = useSelector(
    (state) => state.configurations?.featureToggles?.jackpot ?? false
  )

  // Compute + dispatch on resize and whenever inputs change
  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const recompute = () => {
      const nextLayout = computeLayout({
        layout,
        gameCode,
        vw: window.innerWidth,
        vh: window.innerHeight,
        jackpotEnabled: isJackpotEnabled,
      })
      dispatch(setLayout(nextLayout))
    }

    recompute()
    window.addEventListener('resize', recompute)
    return () => window.removeEventListener('resize', recompute)
  }, [layout, gameCode, isJackpotEnabled, dispatch])
}
